import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  PageWrapper,
  PageHeader,
  PageTitle,
  PageSubtitle,
  SectionGrid,
  SectionCard,
  SectionIcon,
  SectionTitle,
  SectionDescription,
  SectionArrow,
  ThesisBanner,
  ThesisKicker,
  ThesisTitle,
  ThesisBody,
  FunnelRow,
  FunnelStep,
  FunnelConnector,
  FunnelArrow,
  FunnelCaption,
  ThesisActions,
  ThesisCta,
  ThesisLink,
} from './PlaybookPage.styles';

const funnelSteps = [
  { label: 'Elevator Pitch', variant: 'primary' },
  { label: 'Casual Encounter', variant: 'primary', connector: 'earns the first sit-down' },
  { label: 'Problem-Based Meeting', connector: 'differentiates you' },
  { label: 'Revenue', variant: 'success', connector: 'drives' },
];

const sections = [
  {
    title: 'Building the Message',
    description: 'Learn how to create compelling sales decks and messaging that creates corporate credibility and differentiates your company.',
    icon: '\uD83D\uDCE2',
    path: '/playbook/building-message',
    accent: '#3b82f6',
    bg: 'rgba(59, 130, 246, 0.1)',
  },
  {
    title: 'Onboarding / Training',
    description: 'Comprehensive training process for sales team members to master your company messaging and sales techniques.',
    icon: '\uD83C\uDF93',
    path: '/playbook/onboarding-training',
    accent: '#10b981',
    bg: 'rgba(16, 185, 129, 0.1)',
  },
  {
    title: 'Sales Activity',
    description: 'Master the art of prospecting, meeting control, and intentional sales interactions across all communication channels.',
    icon: '\uD83D\uDCC8',
    path: '/playbook/sales-activity',
    accent: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.1)',
  },
  {
    title: 'Closing',
    description: 'Learn to create effective proposals that serve as powerful sales tools and help you close deals successfully.',
    icon: '\uD83E\uDD1D',
    path: '/playbook/closing',
    accent: '#0784a7',
    bg: 'rgba(7, 132, 167, 0.1)',
  },
  {
    title: 'Skills, Orgs & Leadership',
    description: 'Understand compensation plans, organizational structures, and leadership strategies for building high-performing sales teams.',
    icon: '\uD83D\uDC65',
    path: '/playbook/skills-leadership',
    accent: '#8b5cf6',
    bg: 'rgba(139, 92, 246, 0.1)',
  },
];

const PlaybookPage = () => {
  const history = useHistory();

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Sales Playbook</PageTitle>
        <PageSubtitle>
          The Multiply Sales Management Framework. Everything here serves one goal: get more
          meetings, then win them.
        </PageSubtitle>
      </PageHeader>

      <ThesisBanner>
        <ThesisKicker>The Obsession</ThesisKicker>
        <ThesisTitle>Know your intent in every encounter</ThesisTitle>
        <ThesisBody>
          We obsess over two conversations, and they do different jobs. The Elevator Pitch earns
          you the first sit-down: 30 to 60 seconds, and its only job is to get the meeting. The
          Casual Encounter is that sit-down, where your differentiators separate you and earn you
          the problem-based meeting. Problem-based meetings are where solutions are defined and
          closes begin.
        </ThesisBody>
        <FunnelRow>
          {funnelSteps.map((step, index) => (
            <React.Fragment key={step.label}>
              {index > 0 && (
                <FunnelConnector>
                  <FunnelArrow>{'→'}</FunnelArrow>
                  <FunnelCaption>{step.connector}</FunnelCaption>
                </FunnelConnector>
              )}
              <FunnelStep $variant={step.variant}>{step.label}</FunnelStep>
            </React.Fragment>
          ))}
        </FunnelRow>
        <ThesisActions>
          <ThesisCta onClick={() => history.push('/training')}>
            Practice these two scenarios
          </ThesisCta>
          <ThesisLink onClick={() => history.push('/playbook/building-message/anatomy-sales-cycle')}>
            See how they fit the sales cycle {'→'}
          </ThesisLink>
        </ThesisActions>
      </ThesisBanner>

      <SectionGrid>
        {sections.map((section) => (
          <SectionCard
            key={section.path}
            $accent={section.accent}
            onClick={() => history.push(section.path)}
          >
            <SectionIcon $bg={section.bg}>
              {section.icon}
            </SectionIcon>
            <SectionTitle>{section.title}</SectionTitle>
            <SectionDescription>{section.description}</SectionDescription>
            <SectionArrow>Explore {'\u2192'}</SectionArrow>
          </SectionCard>
        ))}
      </SectionGrid>
    </PageWrapper>
  );
};

export default PlaybookPage;
